import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  TextField,
  IconButton,
  Tooltip,
  Button,
  Snackbar,
  Alert,
  Slide,
  SlideProps,
  LinearProgress,
  Skeleton,
  Collapse
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from 'src/store';
import { fetchConversationWithMessages, sendMessage, updateConversationDeliveryState, updateConversation, upsertConversation, toggleOpenConversation, addMessagesToConversation, updateConversationStatus, addMessage } from 'src/features/account/conversations.slice';
import { dateTimeFormatter } from 'src/libs/dateTimeFormatter';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getBranding } from 'src/utils/branding';
import { selectChannelById } from 'src/features/account/channels.slice';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import PersonIcon from '@mui/icons-material/Person';
import Message from '../Channels/Message';
import FunctionMessageGroupV2 from '../Channels/Messages/FunctionMessageGroupV2';
import FollowUpIndicator from '../FollowUpIndicator';
import ConversationOptionsMenu from '../Channels/ConversationOptionsMenu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ConversationFollowUpSettings from '../Channels/ConversationFollowUpSettings';
import DeliveryStateButton from '../Channels/DeliveryStateButton';
import GenerateReplyButton from '../Channels/GenerateReplyButton';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContactMediumIcons from './ContactMediumIcons';
import ConversationEmailParticipants from './ConversationEmailParticipants';
import ConversationParticipants from './ConversationParticipants';
import ConversationQualityTimeline from './ConversationQualityTimeline';
import ClickableContactName from '../common/ClickableContactName';
import { httpClient } from 'src/libs';
import { useContactDrawer } from '../Contacts/ContactDrawer';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

interface ConversationV2Props {
  conversationId: string;
  onClose?: () => void;
}

// Slide transition for snackbars
function SlideTransition(props: Omit<SlideProps, 'direction'>) {
  return <Slide {...props} direction="up" />;
}

const ConversationV2Component: React.FC<ConversationV2Props> = ({ conversationId, onClose }) => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const dispatch = useAppDispatch();
  const { openDrawer } = useContactDrawer();

  /* ----------------------------- Refs & State ---------------------------- */
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);
  const autoScrollRef = useRef(true);
  const fetchedConversationIdsRef = useRef<Set<string>>(new Set());

  const conversation = useAppSelector(state => state.conversations.entities[conversationId]);
  const channel = useAppSelector(state => selectChannelById(state, conversation?.channel_id || ''));
  const user = useAppSelector(state => state.user);
  const advancedMode = user.advancedMode;

  const nextFollowUp = React.useMemo(() => {
    if (conversation?.next_autocomplete_scheduled_at && new Date(conversation.next_autocomplete_scheduled_at) > new Date()) {
      return conversation.next_autocomplete_scheduled_at;
    }
    const pending = conversation?.messages?.filter((m: any) => m.status === 'pending_delivery' && m.deliver_after && new Date(m.deliver_after) > new Date());
    if (pending && pending.length > 0) {
      pending.sort((a: any, b: any) => new Date(a.deliver_after).getTime() - new Date(b.deliver_after).getTime());
      return pending[0].deliver_after;
    }
    return null;
  }, [conversation]);

  /* -------------------- UI/interaction related local state --------------- */
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [sendingError, setSendingError] = useState<string | null>(null);
  const [optimisticMessages, setOptimisticMessages] = useState<any[]>([]);
  const [entityRole, setEntityRole] = useState<'assistant' | 'function' | 'user'>('assistant');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const prevScrollHeightRef = useRef<number | null>(null);
  const [attachmentUrls, setAttachmentUrls] = useState<string[]>([]);
  const [attachmentDisplayUrls, setAttachmentDisplayUrls] = useState<string[]>([]);
  const [activeThumbnailIndex, setActiveThumbnailIndex] = useState<number | null>(null);
    const [timelineExpanded, setTimelineExpanded] = useState(false);

  /* ------------------------ Subject editing local state ------------------------ */
  const [isEditingSubject, setIsEditingSubject] = useState(false);
  const [subjectValue, setSubjectValue] = useState(conversation?.subject || '');
  const [subjectError, setSubjectError] = useState<string | null>(null);
  const [subjectWidth, setSubjectWidth] = useState<number | null>(null);
  const [subjectSaveInfo, setSubjectSaveInfo] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const subjectDisplayRef = useRef<HTMLSpanElement | null>(null);  

  /* ------------------------ Status update state ------------------------ */
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  /* ----------------------------- Derived data ---------------------------- */
  const sendingEntity = conversation?.participating_entities?.find(entity => entity.role === entityRole);
  const userEntity = conversation?.participating_entities?.find(entity => entity.role === 'user');

  const allMessages = React.useMemo(() => {
    const realMessages = conversation?.messages || [];

    // Filter out optimistic messages that already exist in the real list
    // Check by ID first (for temp messages that were replaced), then by content + role + time
    const filteredOptimistic = optimisticMessages.filter(opt => {
      // If optimistic message has been replaced by a real message with same content/role/time, filter it out
      const duplicateExists = realMessages.some(real =>
        real.sender_role === opt.sender_role &&
        real.content === opt.content &&
        Math.abs(new Date(real.created_at).getTime() - new Date(opt.created_at).getTime()) < 60000
      );
      
      return !duplicateExists;
    });

    return [...realMessages, ...filteredOptimistic].filter(m => advancedMode || m.sender_role !== 'function');
  }, [conversation, optimisticMessages, advancedMode]);

  const hasMoreMessages = React.useMemo(() => {
    if (!conversation) return false;
    if (conversation.message_count == null) return false;
    return (conversation.message_count || 0) > allMessages.length;
  }, [conversation, allMessages.length]);

  const conversationReviews = React.useMemo(() => {
    return allMessages
      .map((m: any) => m.metadata?.review)
      .filter((r: any) => !!r);
  }, [allMessages]);

  const generateTempId = () => `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

  // Group consecutive function messages for better display
  const groupMessages = React.useMemo(() => {
    const groups: Array<{ type: 'single' | 'function-group'; messages: any[] }> = [];
    let currentFunctionGroup: any[] = [];

    allMessages.forEach((message: any) => {
      if (message.sender_role === 'function' && !advancedMode) {
        // Skip function messages if not in advanced mode
        return;
      }
      
      if (message.sender_role === 'function') {
        // Add to current function group
        currentFunctionGroup.push(message);
      } else {
        // Non-function message - flush any pending function group
        if (currentFunctionGroup.length > 0) {
          groups.push({ type: 'function-group', messages: currentFunctionGroup });
          currentFunctionGroup = [];
        }
        // Add the non-function message as single
        groups.push({ type: 'single', messages: [message] });
      }
    });

    // Flush any remaining function group
    if (currentFunctionGroup.length > 0) {
      groups.push({ type: 'function-group', messages: currentFunctionGroup });
    }

    return groups;
  }, [allMessages, advancedMode]);

  // Memoize expensive input validations to prevent re-computation on every render
  const isInputValid = React.useMemo(() => {
    return inputValue.trim().length > 0;
  }, [inputValue]);

  const canSendMessage = React.useMemo(() => {
    return isInputValid && !sending && !loading && !!sendingEntity;
  }, [isInputValid, sending, loading, sendingEntity]);

  /* ----------------------------- Side effects ---------------------------- */
  // Fetch conversation data on mount or when the id changes
  useEffect(() => {
    if (!conversationId) return;
    
    // Check if we've already fetched this conversation
    if (fetchedConversationIdsRef.current.has(conversationId)) {
      setLoading(false);
      setIsRefreshing(false);
      setLoadingError(null);
      return;
    }
    
    // Set initial loading states
    setLoading(true);
    setLoadingError(null);
    setIsRefreshing(false);

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const currentController = abortControllerRef.current;

    // Add a timeout fallback to reset loading states
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current && currentController === abortControllerRef.current) {
        console.warn('ConversationV2: Fetch timeout - resetting loading states');
        setLoading(false);
        setIsRefreshing(false);
      }
    }, 10000); // 10 second timeout

    dispatch(fetchConversationWithMessages({ conversationId, messagesLimit: 50 }))
      .then((result) => {
        clearTimeout(timeoutId);
        if (isMountedRef.current && currentController === abortControllerRef.current) {
          setLoading(false);
          setIsRefreshing(false);
          // Only mark as fetched after successful completion
          fetchedConversationIdsRef.current.add(conversationId);
        }
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        if (isMountedRef.current && currentController === abortControllerRef.current && error.name !== 'AbortError') {
          setLoading(false);
          setIsRefreshing(false);
          const errorMessage = error.message || 'Failed to load messages';
          setLoadingError(errorMessage);
          
          // Log the error with more details
          console.error(`ConversationV2: Failed to load conversation ${conversationId}:`, error);
        }
      });

    return () => {
      clearTimeout(timeoutId);
      currentController.abort();
    };
  }, [conversationId, dispatch]); // Simple dependencies - only conversationId and dispatch

  // Focus the input after mount
  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, []);

  // Clean-up
  useEffect(() => {
    console.log(`ConversationV2: Component mounted for conversation ${conversationId}`);
    
    return () => {
      console.log(`ConversationV2: Component unmounting for conversation ${conversationId}, status: ${conversation?.status}`);
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, [conversationId, conversation?.status]);

  // Add a safeguard to prevent the conversation from closing if it's loaded
  useEffect(() => {
    // If we have a valid conversation ID and the component is mounted, ensure it stays open
    if (conversationId && conversation && isMountedRef.current) {
      // Log if the conversation status is closed but we're still trying to view it
      if (conversation.status === 'closed') {
        console.log(`ConversationV2: Viewing a closed conversation ${conversationId} - this is allowed`);
      }
    }
  }, [conversationId, conversation]);

  // Handle user scroll
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    // Enable auto scroll when very near bottom
    autoScrollRef.current = distanceFromBottom < 100;
    setIsNearBottom(distanceFromBottom < 150);

    // Trigger load of older messages when near top
    if (scrollTop < 80 && hasMoreMessages && !loadingOlder && !loading) {
      loadOlderMessages();
    }
  };

  const loadOlderMessages = useCallback(async () => {
    if (!conversation || allMessages.length === 0) return;
    try {
      setLoadingOlder(true);
      // Preserve current scroll height so we can maintain scroll position after prepend
      if (messagesContainerRef.current) {
        prevScrollHeightRef.current = messagesContainerRef.current.scrollHeight;
      }

      const earliestMsg = allMessages[0];
      const params = new URLSearchParams({
        messages_limit: '30',
        last_message_created_at_before: earliestMsg.created_at,
      }).toString();
      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/${conversation.id}?${params}`;
      const response = await httpClient.get(url);
      const newMessages = response.data?.messages || [];

      if (newMessages.length > 0) {
        dispatch(addMessagesToConversation({ conversationId: conversation.id, messages: newMessages }));
      }
    } catch (err) {
      console.error('Failed to load older messages', err);
    } finally {
      setLoadingOlder(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation, allMessages]);

  // Attach scroll handler later in JSX (replace existing Box ref block)
  useEffect(() => {
    if (autoScrollRef.current && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [allMessages.length]);

  // After older messages are prepended, adjust scroll to maintain position
  useEffect(() => {
    if (prevScrollHeightRef.current != null && messagesContainerRef.current) {
      const newHeight = messagesContainerRef.current.scrollHeight;
      const diff = newHeight - prevScrollHeightRef.current;
      messagesContainerRef.current.scrollTop = diff;
      prevScrollHeightRef.current = null;
    }
  }, [allMessages.length]);

  // Keep local subject value in sync when conversation changes
  useEffect(() => {
    setSubjectValue(conversation?.subject || '');
  }, [conversation?.subject]);

  // Reset all local state when conversationId changes (switching between conversations)
  useEffect(() => {
    setInputValue('');
    setSending(false);
    setSendingError(null);
    setOptimisticMessages([]);
    setIsEditingSubject(false);
    setSubjectError(null);
    setSubjectSaveInfo(null);
    setIsUpdatingStatus(false);
    setLoadingError(null);
    setAttachmentUrls([]); // Clear attachments when switching conversations
    setAttachmentDisplayUrls([]);
  }, [conversationId]);



  // Emergency fix - reset sending state if it's stuck
  useEffect(() => {
    if (sending) {
      const stuckTimeout = setTimeout(() => {
        setSending(false);
        setInputValue('');
        setSendingError(null);
        setOptimisticMessages([]);
      }, 5000); // 5 seconds max

      return () => clearTimeout(stuckTimeout);
    }
  }, [sending]);

  // Safety check to reset loading states if they get stuck - only trigger when necessary
  useEffect(() => {
    if (loading || isRefreshing) {
      const safetyTimeout = setTimeout(() => {
        setLoading(false);
        setIsRefreshing(false);
      }, 15000); // 15 second safety timeout

      return () => clearTimeout(safetyTimeout);
    }
  }, [loading, isRefreshing]);

  /* ----------------------- Event handler utilities ----------------------- */
  const handleSendMessage = () => {
    if (!canSendMessage) return;

    setSending(true);
    setSendingError(null);

    const optimisticMessage = {
      id: generateTempId(),
      sender_id: sendingEntity.id,
      content: inputValue,
      created_at: new Date().toISOString(),
      status: 'sending',
      sender_role: entityRole,
      sender_name: sendingEntity.name,
      conversation_id: conversationId,
      isOptimistic: true,
      media_urls: attachmentUrls // Include media URLs in optimistic message
    };
    setOptimisticMessages(prev => [...prev, optimisticMessage]);

    const messageContent = inputValue;
    const messageAttachments = [...attachmentUrls]; // Store attachments before clearing
    
    // Clear input and attachments immediately for better UX
    setInputValue('');
    setAttachmentUrls([]);
    setAttachmentDisplayUrls([]);
    
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const currentController = abortControllerRef.current;

    // AGGRESSIVE TIMEOUT - Reset UI state much sooner
    const emergencyTimeoutId = setTimeout(() => {
      setSending(false);
      setInputValue('');
      setSendingError(null);
      setOptimisticMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
    }, 3000); // 3 second emergency timeout
    
    // Add a timeout to reset sending state as a fallback
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current && currentController === abortControllerRef.current) {
        console.warn('Send message timeout reached, resetting sending state');
        setSending(false);
        setSendingError('Message send timed out');
        setOptimisticMessages(prev => prev.map(msg => msg.id === optimisticMessage.id ? { ...msg, status: 'error', errorDetails: 'Send timeout' } : msg));
      }
    }, 10000); // 10 second timeout

    dispatch(sendMessage({ conversation, message: messageContent, sendingEntity, mediaUrls: messageAttachments }))
      .unwrap()
      .then((result) => {
        clearTimeout(emergencyTimeoutId);
        clearTimeout(timeoutId);
        if (isMountedRef.current) {
          // The sendMessage.fulfilled reducer in the slice now properly handles adding the message
          // from result.data, so we don't need to manually dispatch addMessage anymore
          
          // Remove optimistic message and reset state
          setOptimisticMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
          setSending(false);
          setSendingError(null);
        }
      })
      .catch((error) => {
        clearTimeout(emergencyTimeoutId);
        clearTimeout(timeoutId);
        if (isMountedRef.current && error.name !== 'AbortError') {
          setSending(false);
          const errorMessage = error.message || 'Failed to send message';
          setSendingError(errorMessage);
          setOptimisticMessages(prev => prev.map(msg => msg.id === optimisticMessage.id ? { ...msg, status: 'error', errorDetails: errorMessage } : msg));
        }
      });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      // Allow newline with Shift+Enter, Cmd+Enter (Mac), or Ctrl+Enter (PC)
      if (e.shiftKey || e.metaKey || e.ctrlKey) {
        // For Cmd+Enter and Ctrl+Enter, we need to manually insert the newline
        // because the default behavior doesn't work for these combinations
        if (e.metaKey || e.ctrlKey) {
          e.preventDefault();
          const textarea = e.currentTarget;
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          const value = textarea.value;
          
          // Insert newline at cursor position
          const newValue = value.substring(0, start) + '\n' + value.substring(end);
          setInputValue(newValue);
          
          // Move cursor after the newline
          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd = start + 1;
            // Auto-resize after inserting newline
            textarea.style.height = 'auto';
            textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
          }, 0);
        }
        // Shift+Enter works by default, so we just return for that
        return;
      }
      // Plain Enter sends the message
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleToggleEntityRole = () => {
    const roleOrder: Array<'assistant' | 'user' | 'function'> = ['assistant', 'user', 'function'];
    setEntityRole(prev => {
      const nextIndex = (roleOrder.indexOf(prev) + 1) % roleOrder.length;
      return roleOrder[nextIndex];
    });
  };

  // Handle media upload via Cloudinary
  const handleMediaUpload = () => {
    window.cloudinary.openUploadWidget(
      {
        cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME,
        uploadPreset: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET,
        multiple: true,
        resourceType: 'auto',
        sources: ['local', 'camera'],
        clientAllowedFormats: [
          'png', 'jpg', 'jpeg', 'gif', 'bmp', 'ico',
          'mp4', 'mov', 'avi', 'wmv', 'flv', 'webm', 'mkv',
          'mp3', 'wav', 'm4a', 'flac',
          'pdf'
        ],
        maxFileSize: 100 * 1024 * 1024, // 100MB
        cropping: false,
        showPoweredBy: false,
        styles: {
          palette: {
            window: theme.palette.background.default,
            tabIcon: theme.palette.primary.main,
            menuIcons: theme.palette.secondary.main,
            textDark: theme.palette.text.primary,
            textLight: '#fff',
            link: theme.palette.primary.main,
            inactiveTabIcon: theme.palette.text.disabled,
            error: theme.palette.error.main,
            inProgress: theme.palette.info.main,
            complete: theme.palette.success.main,
            sourceBg: theme.palette.background.paper,
          },
          fonts: {
            default: theme.typography.fontFamily,
          },
          logo: getBranding('logo'),
        },
      },
      (error, result) => {
        if (!error && result.event === 'success') {
          const { public_id, resource_type, format } = result.info;
          const originalUrl = result.info.secure_url;

          let transformation = '';
          let adjusted_resource_type = resource_type;
          let adjusted_format = format;
          let displayUrl = originalUrl;

          if (resource_type === 'image') {
            transformation = 'c_limit,w_1600,h_1600,q_auto:eco,f_auto';
            displayUrl = `https://res.cloudinary.com/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/${resource_type}/upload/${transformation}/${public_id}.${format}`;
          } else if (resource_type === 'video') {
            transformation = 'c_limit,w_1280,h_720,q_auto:eco,f_auto';
            displayUrl = `https://res.cloudinary.com/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/${resource_type}/upload/${transformation}/${public_id}.${format}`;
          } else if (resource_type === 'raw' && format === 'pdf') {
            // For PDFs (including multi-page), create a preview of the first page for display only
            // The original full PDF URL is stored in attachmentUrls and will be sent with the message
            // This ensures multi-page PDFs are fully preserved and sent to recipients
            transformation = 'c_limit,w_800,q_auto:eco,f_auto,page=1';
            adjusted_resource_type = 'image';
            adjusted_format = 'jpg';
            displayUrl = `https://res.cloudinary.com/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/${adjusted_resource_type}/upload/${transformation}/${public_id}.${adjusted_format}`;
          }

          // Store the original URL for sending (preserves full multi-page PDFs)
          setAttachmentUrls(prev => [...prev, originalUrl]);
          // Store the display URL for thumbnails (optimized/preview versions)
          setAttachmentDisplayUrls(prev => [...prev, displayUrl]);
        } else if (error) {
          console.error('Upload error:', error);
        }
      }
    );
  };

  const handleDeleteMedia = (index: number) => {
    setAttachmentUrls(prev => prev.filter((_, i) => i !== index));
    setAttachmentDisplayUrls(prev => prev.filter((_, i) => i !== index)); // Also update display URLs
  };

  // Toggle play / pause for delivery state
  const toggleDeliveryState = () => {
    if (!conversation) return;
    dispatch(updateConversationDeliveryState({
      conversationId: conversation.id,
      channelId: conversation.channel_id,
      deliveryState: conversation.delivery_state === 'pause' ? 'play' : 'pause'
    }));
  };

  const handleRefresh = () => {
    if (isRefreshing || loading) return;

    setIsRefreshing(true);
    setLoadingError(null);

    // Clear this conversation from fetched set to allow refetch
    fetchedConversationIdsRef.current.delete(conversationId);

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const currentController = abortControllerRef.current;

    dispatch(fetchConversationWithMessages({ conversationId, messagesLimit: 50 }))
      .then(() => {
        if (isMountedRef.current && currentController === abortControllerRef.current) {
          setIsRefreshing(false);
          setLoading(false);
          // Re-add to fetched set after successful refresh
          fetchedConversationIdsRef.current.add(conversationId);
        }
      })
      .catch((error) => {
        if (isMountedRef.current && currentController === abortControllerRef.current && error.name !== 'AbortError') {
          setIsRefreshing(false);
          setLoading(false);
          setLoadingError(error.message || 'Failed to load messages');
        }
      });
  };

  const handleRetryFailedMessage = (failedMessage: any) => {
    if (sending) return;

    setOptimisticMessages(prev => prev.map(msg => msg.id === failedMessage.id ? { ...msg, status: 'sending' } : msg));

    dispatch(sendMessage({ conversation, message: failedMessage.content, sendingEntity, mediaUrls: failedMessage.media_urls || [] }))
      .unwrap()
      .then((result) => {
        if (isMountedRef.current) {
          // The sendMessage.fulfilled reducer in the slice now properly handles adding the message
          // from result.data, so we don't need to manually check or add it
          
          setOptimisticMessages(prev => prev.filter(msg => msg.id !== failedMessage.id));
        }
      })
      .catch((error) => {
        if (isMountedRef.current && error.name !== 'AbortError') {
          const errorMessage = error.message || 'Failed to send message';
          setSendingError(errorMessage);
          setOptimisticMessages(prev => prev.map(msg => msg.id === failedMessage.id ? { ...msg, status: 'error', errorDetails: errorMessage } : msg));
        }
      });
  };

  /* ------------------------ Subject editing handlers ------------------------ */
  const handleSaveSubject = () => {
    if (!conversation) return;

    const trimmed = subjectValue.trim();
    if (trimmed === conversation.subject) {
      setIsEditingSubject(false);
      return;
    }

    if (!trimmed) {
      setSubjectError('Subject cannot be empty');
      return;
    }

    setSubjectError(null);

    dispatch(updateConversation({
      conversationId: conversation.id,
      channelId: conversation.channel_id,
      conversationData: { subject: trimmed }
    }))
      .unwrap()
      .then(() => {
        // Optimistically update local store
        setIsEditingSubject(false);
        setSubjectSaveInfo({ type: 'success', message: 'Subject updated' });
      })
      .catch((err) => {
        const msg = err?.message || 'Failed to update subject';
        setSubjectError(msg);
        setSubjectSaveInfo({ type: 'error', message: msg });
      });
  };

  const handleCancelEditSubject = () => {
    setIsEditingSubject(false);
    setSubjectValue(conversation?.subject || '');
    setSubjectError(null);
  };

  /* -------------------------------- Render ------------------------------- */
  return (
    <Paper
      elevation={6}
      sx={{
        height: '100%',
        maxHeight: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: `1px solid ${darkMode ? theme.palette.grey[700] : theme.palette.grey[300]}`,
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          // Add top padding for PWA mode to prevent header cutoff
          paddingTop: {
            xs: 'calc(16px + env(safe-area-inset-top, 0px))', // Mobile: add safe area padding
            sm: 2 // Desktop: normal padding
          },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${darkMode ? theme.palette.grey[800] : theme.palette.grey[200]}`,
          bgcolor: theme.palette.background.glassHeavy,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Status gradient overlay */}
        {conversation?.status && theme.statusGradients?.[conversation.status] && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: theme.statusGradients[conversation.status],
              opacity: darkMode ? 0.15 : 0.08,
              pointerEvents: 'none',
              zIndex: 0,
              ...(conversation.status === 'loading' || conversation.status === 'loading_messages' || conversation.status === 'pending_assistant_reply' ? {
                animation: 'gradientShift 3s ease infinite',
                '@keyframes gradientShift': {
                  '0%, 100%': { opacity: darkMode ? 0.15 : 0.08 },
                  '50%': { opacity: darkMode ? 0.25 : 0.15 },
                },
              } : {}),
              ...(conversation.status === 'error' || conversation.status === 'needs_attention' ? {
                animation: 'gradientPulse 2s ease infinite',
                '@keyframes gradientPulse': {
                  '0%, 100%': { opacity: darkMode ? 0.15 : 0.08 },
                  '50%': { opacity: darkMode ? 0.3 : 0.2 },
                },
              } : {}),
            }}
          />
        )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0, zIndex: 1 }}>
      <Box sx={{ position: 'relative' }}>
      <ConversationParticipants
      participatingEntities={conversation?.participating_entities}
      maxAvatars={2}
      avatarSize="large"
      showOthersCount={false}
      />
      {/* Online indicator */}
      <Box
      sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: theme.socialColors?.twitter || '#1d9bf0',
                border: '2px solid black'
              }}
            />

            {/* Contact mediums overlay */}
            {conversation?.participating_entities && (
              <Box sx={{ position: 'absolute', bottom: -4, right: -4 }}>
                <ContactMediumIcons participatingEntities={conversation.participating_entities} size={12} />
              </Box>
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <ClickableContactName
                  contactId={userEntity?.contact_id}
                  contactName={userEntity?.name || 'Unknown Contact'}
                  variant="h6"
                  sx={{ 
                    fontSize: '1.25rem', 
                    color: darkMode ? 'white' : theme.palette.text.primary,
                    fontWeight: 500
                  }}
                />
                <ConversationParticipants
                  participatingEntities={conversation?.participating_entities}
                  maxAvatars={0}
                  avatarSize="small"
                  showOthersCount={true}
                />
              </Box>
            </Box>

            {/* Subject line with inline editing */}
            {isEditingSubject ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextField
                  value={subjectValue}
                  onChange={(e) => setSubjectValue(e.target.value)}
                  size="small"
                  variant="standard"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveSubject();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      handleCancelEditSubject();
                    }
                  }}
                  sx={{
                    width: subjectWidth ? `${subjectWidth}px` : `${Math.max(subjectValue.length + 2, 10)}ch`,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: darkMode ? theme.palette.grey[700] : theme.palette.grey[300],
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: entityRole === 'assistant' ? theme.palette.primary.main : entityRole === 'user' ? theme.palette.success.main : theme.palette.secondary.main,
                      borderWidth: 2,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: entityRole === 'assistant' ? alpha(theme.palette.primary.main, 0.7) : entityRole === 'user' ? alpha(theme.palette.success.main, 0.7) : alpha(theme.palette.secondary.main, 0.7),
                    },
                    transition: 'all 0.2s ease',
                  }}
                  onInput={(e) => {
                    const target = e.target as HTMLInputElement;
                    setSubjectWidth(target.scrollWidth);
                  }}
                />
                <IconButton size="small" color="primary" onClick={handleSaveSubject}>
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleCancelEditSubject}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  '&:hover .edit-icon': {
                    opacity: 1
                  }
                }}
              >
                <Typography
                  ref={subjectDisplayRef}
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    fontSize: '1rem',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    flexShrink: 1
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (subjectDisplayRef.current) {
                      setSubjectWidth(subjectDisplayRef.current.offsetWidth);
                    }
                    setIsEditingSubject(true);
                  }}
                >
                  {conversation?.subject || '(No subject)'}
                </Typography>
                <Tooltip title="Edit subject" arrow>
                  <IconButton
                    className="edit-icon"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (subjectDisplayRef.current) {
                        setSubjectWidth(subjectDisplayRef.current.offsetWidth);
                      }
                      setIsEditingSubject(true);
                    }}
                    sx={{ opacity: 0, transition: 'opacity 0.2s' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, zIndex: 1 }}>
          {/* Email participants display */}
          {conversation?.metadata?.email_participants && (
            <ConversationEmailParticipants
              conversationId={conversation.id}
              channelId={conversation.channel_id}
              emailParticipants={conversation.metadata.email_participants}
              conversationMetadata={conversation.metadata}
              maxAvatars={2}
            />
          )}
          {nextFollowUp && <FollowUpIndicator futureTime={nextFollowUp} size={18} />}
          <ConversationOptionsMenu conversation={conversation} sx={{ p:0 }} triggerIcon={<MoreVertIcon fontSize="small" />} />
        </Box>
      </Box>


        {(conversationReviews?.length ?? 0) > 1 && (
          <Box sx={{ bgcolor: theme.palette.background.glassHeavy }}>
            <ConversationQualityTimeline
              reviews={conversationReviews ?? []}
              options={{ startCollapsed: true, collapsedHeight: 50, collapsedOpacity: 0.5, expandOnClick: true }}
            />
          </Box>
        )}
    
      {/* Messages container */}
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          pb: { xs: 0, sm: 2 },
          pt: 2,
          pl: { xs: 0, sm: 2 },
          pr: { xs: 0, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.palette.background.glassHeavy,
          backgroundSize: 'cover',
          // Tighter spacing between consecutive function messages
          '& [data-message-role="function"] + [data-message-role="function"]': {
            mt: -0.75, // Pull consecutive function messages closer
          },
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'transparent',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: darkMode ? alpha(theme.palette.grey[600], 0.5) : alpha(theme.palette.grey[400], 0.5),
            borderRadius: '4px',
            '&:hover': {
              bgcolor: darkMode ? theme.palette.grey[600] : theme.palette.grey[400],
            }
          }
        }}
        onScroll={handleScroll}
      >
        {loading && allMessages.length === 0 && conversation?.status !== 'draft' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            {[...Array(4)].map((_, idx) => (
              <Skeleton key={idx} variant="rounded" height={60} animation="wave" />
            ))}
          </Box>
        ) : loadingError ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 2 }}>
            <Alert severity="error" sx={{ width: '90%' }}>
              {loadingError}
            </Alert>
            <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={handleRefresh}>
              Retry
            </Button>
          </Box>
        ) : allMessages.length === 0 && !fetchedConversationIdsRef.current.has(conversationId) && conversation?.status !== 'draft' ? (
          // Show skeleton while we haven't fetched yet (but not for drafts)
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            {[...Array(4)].map((_, idx) => (
              <Skeleton key={idx} variant="rounded" height={60} animation="wave" />
            ))}
          </Box>
        ) : allMessages.length === 0 ? (
          // Only show "No messages" if we've actually fetched and found none
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 2 }}>
            <img
              src={getBranding('oppy_expressions_no_mouth')}
              alt="No messages"
              style={{ width: 80, height: 80, opacity: 0.5 }}
            />
            <Typography variant="body2" color="text.secondary" align="center">
              No messages yet. Start the conversation!
            </Typography>
          </Box>
        ) : (
          <React.Fragment>
            {loadingOlder && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {groupMessages.map((group, index) => {
              if (group.type === 'function-group') {
                // Render grouped function messages
                if (!group.messages || group.messages.length === 0) return null;
                return (
                  <FunctionMessageGroupV2
                    key={`function-group-${index}-${group.messages[0]?.id || index}`}
                    messages={group.messages}
                    channelId={channel?.id || conversation?.channel_id}
                    conversationDeliveryState={conversation?.delivery_state}
                  />
                );
              } else {
                // Render single message (non-function)
                if (!group.messages || group.messages.length === 0) return null;
                const msg = group.messages[0];
                return (
                  <Message
                    key={msg.id}
                    message={msg}
                    sendingEntity={sendingEntity}
                    channelId={channel?.id || conversation?.channel_id}
                    conversationDeliveryState={conversation?.delivery_state}
                    variant="compact"
                    fetchConversationsWithMessagesAction={msg.isOptimistic && msg.status === 'error' ? () => handleRetryFailedMessage(msg) : undefined}
                    isOptimistic={msg.isOptimistic || false}
                    parentConversation={conversation}
                  />
                );
              }
            })}
          </React.Fragment>
        )}

        {/* Follow-up settings and controls */}
        {conversation && (
          <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', mt:2, gap:1 }}>
            <ConversationFollowUpSettings conversationId={conversation.id} />
            <Box sx={{ display:'flex', flexDirection:'row', alignItems:'center', gap:1 }}>
              <GenerateReplyButton conversation={conversation} />
              <DeliveryStateButton
                deliveryState={conversation.delivery_state as 'play' | 'pause' | 'loading' | 'stop'}
                onClick={toggleDeliveryState}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Floating controls (close + scroll down) */}
      {/* Close / Back button */}
      <Tooltip title="Close" arrow>
        <IconButton
          onClick={() => {
            if (onClose) {
              onClose();
            } else {
              // Fallback if no onClose handler provided
              dispatch(toggleOpenConversation(null));
              if (typeof window !== 'undefined') {
                window.history.pushState({}, '', window.location.pathname);
              }
            }
          }}
          sx={{
            position: 'absolute',
            bottom: 96,
            left: 24,
            zIndex: 1200,
            bgcolor: theme.palette.grey[700],
            color: 'common.white',
            boxShadow: 3,
            '&:hover': { bgcolor: theme.palette.grey[800] },
            height: 46,
            width: 46,
            border: theme => `1px solid ${alpha(theme.palette.common.white, 0.4)}`
          }}
        >
          <CloseIcon />
        </IconButton>
      </Tooltip>

      {/* Scroll-to-bottom FAB */}
      {!isNearBottom && (
        <Tooltip title="Jump to latest" arrow>
          <IconButton
            onClick={() => {
              if (messagesContainerRef.current) {
                messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
              }
              setIsNearBottom(true);
            }}
            sx={{
              position: 'absolute',
              bottom: 96,
              right: 24,
              zIndex: 1200,
              bgcolor: theme.palette.grey[700],
              color: 'common.white',
              boxShadow: 3,
              '&:hover': { bgcolor: theme.palette.grey[800] },
              height: 46,
              width: 46,
              border: theme => `1px solid ${alpha(theme.palette.common.white, 0.4)}`
            }}
          >
            <ArrowDownwardIcon />
          </IconButton>
        </Tooltip>
      )}

      {/* Input Box */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${darkMode ? theme.palette.grey[800] : theme.palette.grey[200]}`,
          bgcolor: darkMode ? theme.palette.background.glassHeavy : theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          backdropFilter: 'blur(10px)',
          position: 'relative',
        }}
      >

        {/* Attachment Thumbnails */}
        {attachmentDisplayUrls.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              p: 1,
              backgroundColor: darkMode ? alpha(theme.palette.grey[900], 0.8) : alpha(theme.palette.grey[100], 0.8),
              borderRadius: 1,
              border: 1,
              borderColor: darkMode ? theme.palette.grey[800] : theme.palette.grey[200],
            }}
          >
            {attachmentDisplayUrls.map((url, index) => (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  width: 80,
                  height: 80,
                  bgcolor: darkMode ? theme.palette.grey[800] : theme.palette.grey[50],
                  borderRadius: 1,
                  border: 1,
                  borderColor: darkMode ? theme.palette.grey[700] : theme.palette.grey[300],
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: darkMode ? theme.palette.grey[600] : theme.palette.grey[400],
                  },
                }}
                onMouseEnter={() => setActiveThumbnailIndex(index)}
                onMouseLeave={() => setActiveThumbnailIndex(null)}
              >
                {(() => {
                  if (/\.(png|jpg|jpeg|gif)$/i.test(url)) {
                    return (
                      <img
                        src={url}
                        alt={`Attachment ${index + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 }}
                      />
                    );
                  } else if (/\.(mp4|mov|avi|wmv|flv|webm|mkv)$/i.test(url)) {
                    return (
                      <video
                        width="100%"
                        height="100%"
                        style={{ objectFit: 'contain', borderRadius: 4 }}
                      >
                        <source src={url} type="video/mp4" />
                      </video>
                    );
                  } else if (/\.(pdf)$/i.test(url)) {
                    return (
                      <Tooltip title="PDF preview - full document will be sent" placement="top" arrow>
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: darkMode ? theme.palette.grey[800] : theme.palette.grey[200],
                            color: darkMode ? theme.palette.grey[300] : theme.palette.grey[700],
                            borderRadius: 1,
                          }}
                        >
                          <PictureAsPdfIcon sx={{ fontSize: 32 }} />
                        </Box>
                      </Tooltip>
                    );
                  } else {
                    return (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: darkMode ? theme.palette.grey[800] : theme.palette.grey[200],
                          color: darkMode ? theme.palette.grey[300] : theme.palette.grey[700],
                          borderRadius: 1,
                        }}
                      >
                        <InsertDriveFileIcon sx={{ fontSize: 32 }} />
                      </Box>
                    );
                  }
                })()}
                {activeThumbnailIndex === index && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                    }}
                  >
                    <IconButton
                      size="small"
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        padding: '2px',
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.85)',
                        },
                      }}
                      onClick={() => handleDeleteMedia(index)}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            bgcolor: darkMode ? alpha(theme.palette.grey[800], 0.5) : alpha(theme.palette.grey[100], 0.5),
            borderRadius: 3,
            p: 0.5,
          }}
        >
          {/* Entity toggle button (left side) */}
          <Tooltip
            title={(() => {
              if (entityRole === 'assistant') return `Currently sending as ${channel?.chatbot_name || 'Assistant'}. Click to switch to User mode.`;
              if (entityRole === 'user') return 'Currently sending as User. Click to switch to Function mode.';
              return 'Currently sending as Function. Click to switch to Assistant mode.';
            })()}
            placement="top"
          >
            <IconButton
              onClick={handleToggleEntityRole}
              size="small"
              sx={{
                ml: 0.5,
                color: entityRole === 'assistant' 
                  ? theme.palette.primary.main 
                  : entityRole === 'user' 
                  ? theme.palette.success.main 
                  : theme.palette.secondary.main,
                '&:hover': {
                  bgcolor: alpha(
                    entityRole === 'assistant'
                      ? theme.palette.primary.main
                      : entityRole === 'user'
                      ? theme.palette.success.main
                      : theme.palette.secondary.main,
                    0.1
                  ),
                }
              }}
            >
              {entityRole === 'assistant' ? (
                <img
                  src={getBranding('oppy_expressions_no_mouth')}
                  alt="Assistant Icon"
                  style={{
                    width: '20px',
                    height: '20px',
                    filter: darkMode ? 'drop-shadow(0 0 2px rgba(255,255,255,0.3))' : 'none'
                  }}
                />
              ) : entityRole === 'user' ? (
                <PersonIcon fontSize="small" />
              ) : (
                <TipsAndUpdatesIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          {/* Text input (full width) */}
          <textarea
            ref={inputRef}
            placeholder={entityRole === 'assistant'
              ? `Message as ${channel?.chatbot_name || 'Assistant'}...`
              : entityRole === 'user'
              ? 'Write as the contact...'
              : 'Internal prompt...'}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (sendingError) setSendingError(null);
              // Auto-resize textarea
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = Math.min(target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={handleKeyDown}
            disabled={sending}
            rows={1}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              backgroundColor: 'transparent',
              color: darkMode ? '#ffffff' : '#000000',
              fontSize: '16px', // Prevent mobile browsers (iOS Safari) from zooming the input on focus
              fontFamily: theme.typography.fontFamily,
              padding: '8px',
              resize: 'none',
              minHeight: '24px',
              maxHeight: '120px',
              overflowY: 'auto',
              lineHeight: '1.5',
            }}
          />

          {/* Attachment button (right side) */}
          <Tooltip
            title="Attach files"
            placement="top"
          >
            <IconButton
              onClick={handleMediaUpload}
              disabled={sending || loading}
              size="small"
              sx={{
                mr: 0.5,
                color: darkMode ? theme.palette.grey[400] : theme.palette.grey[600],
                '&:hover': {
                  bgcolor: alpha(theme.palette.grey[700], 0.1),
                }
              }}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Send button */}
          <IconButton
            onClick={handleSendMessage}
            disabled={!canSendMessage}
            size="small"
            sx={{
              mr: 0.5,
              bgcolor: canSendMessage 
                ? (entityRole === 'assistant' ? theme.palette.primary.main : entityRole === 'user' ? theme.palette.success.main : theme.palette.secondary.main)
                : 'transparent',
              color: canSendMessage
                ? '#ffffff'  // Always white when enabled
                : darkMode ? theme.palette.grey[500] : theme.palette.grey[400],
              '&:hover': {
                bgcolor: canSendMessage
                  ? (entityRole === 'assistant' ? theme.palette.primary.dark : entityRole === 'user' ? theme.palette.success.dark : theme.palette.secondary.dark)
                  : alpha(theme.palette.grey[700], 0.1),
              },
              '&.Mui-disabled': {
                bgcolor: 'transparent',
                color: darkMode ? theme.palette.grey[600] : theme.palette.grey[400],
              },
            }}
          >
            {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
          </IconButton>
        </Box>
    </Box>

      {/* Error snackbar */}
      <Snackbar
        open={!!sendingError}
        autoHideDuration={5000}
        onClose={() => setSendingError(null)}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSendingError(null)} severity="error" sx={{ width: '100%' }}>
          {sendingError}
        </Alert>
      </Snackbar>

      {/* Subject save feedback snackbar */}
      <Snackbar
        open={!!subjectSaveInfo}
        autoHideDuration={4000}
        onClose={() => setSubjectSaveInfo(null)}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {subjectSaveInfo && (
          <Alert
            onClose={() => setSubjectSaveInfo(null)}
            severity={subjectSaveInfo.type}
            sx={{ width: '100%' }}
          >
            {subjectSaveInfo.message}
          </Alert>
        )}
      </Snackbar>
    </Paper>
  );
};

// Memoize the component to prevent re-renders when URL params change (e.g., opening ContactDrawer)
const ConversationV2 = React.memo(ConversationV2Component, (prevProps, nextProps) => {
  // Only re-render if conversationId or onClose changes
  return (
    prevProps.conversationId === nextProps.conversationId &&
    prevProps.onClose === nextProps.onClose
  );
});

export default ConversationV2; 
